/**
 * Canonical JSON and a pure SHA-256 (FIPS 180-4). Core may not touch WebCrypto (async, I/O-shaped), so the
 * proposal `contentHash` and deterministic id derivation use this implementation. It is small, synchronous and
 * verified against the standard test vectors and node:crypto in the tests.
 */

/** JSON with object keys sorted recursively; `undefined` members are dropped (exact optional keys). */
export const canonicalJson = (value: unknown): string => {
  if (value === null) return "null"
  switch (typeof value) {
    case "number":
      if (!Number.isFinite(value)) throw new Error("canonicalJson: non-finite number")
      return JSON.stringify(value)
    case "boolean":
    case "string":
      return JSON.stringify(value)
    case "bigint":
      throw new Error("canonicalJson: bigint")
    case "object": {
      if (Array.isArray(value))
        return `[${value.map((v) => (v === undefined ? "null" : canonicalJson(v))).join(",")}]`
      const obj = value as Record<string, unknown>
      const keys = Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .sort()
      return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(",")}}`
    }
    default:
      throw new Error(`canonicalJson: unsupported ${typeof value}`)
  }
}

/** UTF-8 encode without TextEncoder (not in the ES2023 lib). Lone surrogates become U+FFFD, like TextEncoder. */
export const utf8 = (s: string): Uint8Array => {
  const out: number[] = []
  for (let i = 0; i < s.length; i++) {
    let c = s.charCodeAt(i)
    if (c >= 0xd800 && c <= 0xdbff) {
      const n = i + 1 < s.length ? s.charCodeAt(i + 1) : 0
      if (n >= 0xdc00 && n <= 0xdfff) {
        c = 0x10000 + ((c - 0xd800) << 10) + (n - 0xdc00)
        i++
      } else c = 0xfffd
    } else if (c >= 0xdc00 && c <= 0xdfff) c = 0xfffd
    if (c < 0x80) out.push(c)
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63))
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63))
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63))
  }
  return Uint8Array.from(out)
}

const K = Uint32Array.from([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
  0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
  0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
  0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
])

/** SHA-256 of bytes (or of the UTF-8 encoding of a string), as lowercase hex. */
export const sha256Hex = (input: Uint8Array | string): string => {
  const msg = typeof input === "string" ? utf8(input) : input
  const bitLen = msg.length * 8
  const padded = new Uint8Array(((msg.length + 9 + 63) >> 6) << 6)
  padded.set(msg)
  padded[msg.length] = 0x80
  const dv = new DataView(padded.buffer)
  dv.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000))
  dv.setUint32(padded.length - 4, bitLen >>> 0)
  const h = Uint32Array.from([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ])
  const w = new Uint32Array(64)
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n))
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4)
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15] ?? 0
      const b = w[i - 2] ?? 0
      const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3)
      const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10)
      w[i] = ((w[i - 16] ?? 0) + s0 + (w[i - 7] ?? 0) + s1) >>> 0
    }
    let a = h[0] ?? 0
    let b = h[1] ?? 0
    let c = h[2] ?? 0
    let d = h[3] ?? 0
    let e = h[4] ?? 0
    let f = h[5] ?? 0
    let g = h[6] ?? 0
    let hh = h[7] ?? 0
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)
      const ch = (e & f) ^ (~e & g)
      const t1 = (hh + S1 + ch + (K[i] ?? 0) + (w[i] ?? 0)) >>> 0
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) >>> 0
      hh = g
      g = f
      f = e
      e = (d + t1) >>> 0
      d = c
      c = b
      b = a
      a = (t1 + t2) >>> 0
    }
    h[0] = ((h[0] ?? 0) + a) >>> 0
    h[1] = ((h[1] ?? 0) + b) >>> 0
    h[2] = ((h[2] ?? 0) + c) >>> 0
    h[3] = ((h[3] ?? 0) + d) >>> 0
    h[4] = ((h[4] ?? 0) + e) >>> 0
    h[5] = ((h[5] ?? 0) + f) >>> 0
    h[6] = ((h[6] ?? 0) + g) >>> 0
    h[7] = ((h[7] ?? 0) + hh) >>> 0
  }
  return Array.from(h, (x) => x.toString(16).padStart(8, "0")).join("")
}

/** sha256 over the canonical JSON of an (encoded) value. Used for proposal content hashes and pack hashes. */
export const contentHash = (encodedBody: unknown): string => sha256Hex(canonicalJson(encodedBody))

const CROCKFORD = "0123456789abcdefghjkmnpqrstvwxyz"

/** 10-char lowercase Crockford base32 encoding of a millisecond timestamp (the ULID time part). */
export const ulidTime = (ms: number): string => {
  let t = Math.max(0, Math.floor(ms))
  let s = ""
  for (let i = 0; i < 10; i++) {
    s = CROCKFORD[t % 32] + s
    t = Math.floor(t / 32)
  }
  return s
}

/** Pure ULID from a timestamp and 10 random bytes (the impure inputs are passed in). */
export const mintId = (prefix: string, nowMs: number, random10: Uint8Array): string => {
  let bits = 0n
  for (let i = 0; i < 10; i++) bits = (bits << 8n) | BigInt(random10[i] ?? 0)
  let rand = ""
  for (let i = 0; i < 16; i++) {
    rand = CROCKFORD[Number(bits & 31n)] + rand
    bits >>= 5n
  }
  return `${prefix}_${ulidTime(nowMs)}${rand}`
}

/**
 * Deterministic id for records `decide` must create without a client-minted id (escalation proposals, the
 * proposal part of an apply_allowed changeset, adopted changesets). Same inputs → same id, so a replayed or
 * dry-run decide produces identical events.
 */
export const deriveId = <T extends string>(prefix: string, nowMs: number, seed: string): T => {
  const hex = sha256Hex(`${prefix}:${seed}`)
  const bytes = new Uint8Array(10)
  for (let i = 0; i < 10; i++) bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return mintId(prefix, nowMs, bytes) as T
}
